var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import { Box, Grid, Skeleton, Typography, useTheme, useMediaQuery, Button, } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
export const SecondaryGrid = (_a) => {
    var _b, _c, _d, _e, _f;
    var { sx, slotProps, gridMaxHeight = "calc(100dvh - 200px)", fillRemainingSpace = true, headerBackgroundColor, useAutoHeight = true, renderCard, localeText } = _a, props = __rest(_a, ["sx", "slotProps", "gridMaxHeight", "fillRemainingSpace", "headerBackgroundColor", "useAutoHeight", "renderCard", "localeText"]);
    const { t } = useTranslation("common");
    const theme = useTheme();
    const showCardView = !!renderCard && useMediaQuery(theme.breakpoints.down("lg"));
    const mobileCardEndSpacerSx = {
        height: {
            xs: 16,
            sm: 12,
            md: 8,
        },
    };
    const resolvedAutoHeight = fillRemainingSpace ? false : useAutoHeight;
    const mergedSlotProps = Object.assign(Object.assign({}, slotProps), { loadingOverlay: Object.assign({ variant: "skeleton", noRowsVariant: "skeleton" }, slotProps === null || slotProps === void 0 ? void 0 : slotProps.loadingOverlay) });
    const mergedLocaleText = Object.assign({ paginationRowsPerPage: t("pagination.rowsPerPage") }, localeText);
    const rows = props.rows || [];
    const loading = (_b = props.loading) !== null && _b !== void 0 ? _b : false;
    const isServerPagination = props.paginationMode === "server";
    const paginationModel = props.paginationModel;
    const page = (_c = paginationModel === null || paginationModel === void 0 ? void 0 : paginationModel.page) !== null && _c !== void 0 ? _c : 0;
    const pageSize = (_d = paginationModel === null || paginationModel === void 0 ? void 0 : paginationModel.pageSize) !== null && _d !== void 0 ? _d : 25;
    const rowCount = isServerPagination ? ((_e = props.rowCount) !== null && _e !== void 0 ? _e : rows.length) : rows.length;
    const [accumulatedRows, setAccumulatedRows] = React.useState(rows);
    React.useEffect(() => {
        if (!showCardView || !isServerPagination) {
            setAccumulatedRows(rows);
            return;
        }
        // Keep current cards visible while requesting the next page.
        if (loading && rows.length === 0) {
            return;
        }
        if (page === 0) {
            setAccumulatedRows(rows);
            return;
        }
        setAccumulatedRows((prev) => {
            const merged = [...prev];
            const ids = new Set(prev.map((item) => item === null || item === void 0 ? void 0 : item.id));
            rows.forEach((row) => {
                if (!ids.has(row === null || row === void 0 ? void 0 : row.id)) {
                    merged.push(row);
                    ids.add(row === null || row === void 0 ? void 0 : row.id);
                }
            });
            return merged;
        });
    }, [showCardView, isServerPagination, loading, rows, page]);
    const cardRows = showCardView && isServerPagination ? accumulatedRows : rows;
    const canLoadMore = isServerPagination && cardRows.length < rowCount;
    const shouldShowInitialCardSkeleton = loading && page === 0 && cardRows.length === 0;
    const handleLoadMore = () => {
        if (loading || !canLoadMore || !props.onPaginationModelChange)
            return;
        props.onPaginationModelChange({ page: page + 1, pageSize }, {});
    };
    if (showCardView) {
        return (React.createElement(Box, { sx: { mt: 1.5 } },
            shouldShowInitialCardSkeleton ? (React.createElement(Grid, { container: true, spacing: 1.5 }, Array.from({ length: 6 }).map((_, i) => (React.createElement(Grid, { key: i, size: { xs: 12, sm: 6, md: 4 } },
                React.createElement(Skeleton, { variant: "rounded", height: 160, sx: { borderRadius: 3 } })))))) : cardRows.length === 0 ? (React.createElement(Box, { sx: { textAlign: "center", py: 6 } },
                React.createElement(Typography, { color: "text.secondary" }, (_f = localeText === null || localeText === void 0 ? void 0 : localeText.noRowsLabel) !== null && _f !== void 0 ? _f : "No rows"))) : (React.createElement(Grid, { container: true, spacing: 1.5 }, cardRows.map((row) => (React.createElement(Grid, { key: row.id, size: { xs: 12, sm: 6, md: 4 } }, renderCard(row)))))),
            canLoadMore && (React.createElement(Box, { sx: { mt: 2, display: "flex", justifyContent: "center" } },
                React.createElement(Button, { variant: "outlined", size: "small", disabled: loading, onClick: handleLoadMore }, loading ? "Loading..." : "Load more")))));
    }
    return (React.createElement(Box, { sx: fillRemainingSpace
            ? {
                mt: 1.5,
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
            }
            : undefined },
        React.createElement(DataGrid, Object.assign({ autoHeight: resolvedAutoHeight, checkboxSelection: false, disableColumnMenu: true, sx: fillRemainingSpace
                ? Object.assign(Object.assign({ height: "100%", minHeight: 0, width: "100%" }, (headerBackgroundColor
                    ? {
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: headerBackgroundColor,
                        },
                        "& .MuiDataGrid-columnHeader": {
                            backgroundColor: headerBackgroundColor,
                        },
                    }
                    : {})), sx) : Object.assign(Object.assign(Object.assign({ mt: 1.5 }, (resolvedAutoHeight
                ? { maxHeight: gridMaxHeight }
                : { height: gridMaxHeight })), (headerBackgroundColor
                ? {
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: headerBackgroundColor,
                    },
                    "& .MuiDataGrid-columnHeader": {
                        backgroundColor: headerBackgroundColor,
                    },
                }
                : {})), sx), slotProps: mergedSlotProps, localeText: mergedLocaleText }, props))));
};
export default SecondaryGrid;
